// import node module libraries
import React, { Fragment, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Image, Dropdown, Form, Badge } from "react-bootstrap";
import { XCircle, MoreVertical, CheckCircle, Clock, Eye, Mail, Award, FileText } from "react-feather";

// import custom components
import DotBadge from "components/elements/bootstrap/DotBadge";
import TanstackTable from "components/elements/advance-table/TanstackTable";
import { formatDate, truncateTextByCount } from "helper/utils";
import { Avatar } from "components/elements/bootstrap/Avatar";
import AlertModal from "components/elements/modal-alert/AlertModal";
import CourseFeedbackFormModal from "components/dashboard/courses/CourseFeedbackFormModal";
import { PATH_ADMIN } from "routes/Paths";
import { useMutation } from "@apollo/client/react";
import { REJECT_COURSE, UPDATE_COURSE_ACTIVATION, SEND_COURSE_FEEDBACK_EMAILS, SEND_COURSE_CERTIFICATES } from "helper/graphql/mutations";
import { Notify } from "notiflix";
import moment from "moment";

const getStatusColor = (status) => {
  if (status.toLowerCase() === "draft") {
    return 'warning'
  } else if (status.toLowerCase() === "active") {
    return 'success'
  } else if (status.toLowerCase() === "rejected") {
    return 'danger'
  } else {
    return 'info'
  }
}

const CoursesTable = ({ courses_data }) => {
  const navigate = useNavigate();
  
  // The forwardRef is important!!
  const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <Link
      to="#"
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      className="btn-icon btn btn-ghost btn-sm rounded-circle action-toggle"
    >
      {children}
    </Link>
  ));

  const ActionMenu = ({ course }) => {
    const [showReject, setShowReject] = useState(false);
    const [showAccept, setShowAccept] = useState(false);
    const [showFeedbackForm, setShowFeedbackForm] = useState(false);
    const [showSendFeedback, setShowSendFeedback] = useState(false);
    const [showSendCerts, setShowSendCerts] = useState(false);
    const [rejectReason, setRejectReason] = useState('');

    const [onUpdateCourseActivation, {loading: isUpdating}] = useMutation(UPDATE_COURSE_ACTIVATION);
    const [onRejectCourse, {loading: isRejecting}] = useMutation(REJECT_COURSE);
    const [onSendFeedbackEmails, {loading: isSendingFeedback}] = useMutation(SEND_COURSE_FEEDBACK_EMAILS);
    const [onSendCertificates, {loading: isSendingCerts}] = useMutation(SEND_COURSE_CERTIFICATES);

    const handleSendFeedbackEmails = async () => {
      try {
        const res = await onSendFeedbackEmails({ variables: { courseGuid: course.guid } });
        const count = res?.data?.sendCourseFeedbackEmails?.sentCount ?? 0;
        Notify.success(`Feedback emails sent to ${count} student${count !== 1 ? 's' : ''}.`);
        setShowSendFeedback(false);
      } catch (err) {
        Notify.failure(err?.message || 'Failed to send feedback emails.');
      }
    };

    const handleSendCertificates = async () => {
      try {
        const res = await onSendCertificates({ variables: { courseGuid: course.guid } });
        const count = res?.data?.sendCourseCertificates?.sentCount ?? 0;
        Notify.success(`Certificates sent to ${count} student${count !== 1 ? 's' : ''}.`);
        setShowSendCerts(false);
      } catch (err) {
        Notify.failure(err?.message || 'Failed to send certificates.');
      }
    };

    const handleUpdate = (status, isActive) => {
      onUpdateCourseActivation({
        variables: { 
          guid: course?.guid, 
          isActive: isActive, 
          status: status,
          title: course?.title,
          description: course?.description, 
          language: course?.language, 
          dificultyLevel: course?.dificultyLevel || '', 
          creatorId: course?.creator?.guid, 
          courseStartDate: course?.courseStartDate, 
          courseEndDate: course?.courseEndDate,          
        },
        onCompleted: () => {
          Notify.success("Course approved successfully!");
          window.location.reload();
          setShowAccept(false);
        },
        onError: () => {
          Notify.failure("Failed to update course!");
        },
      });
    };

    const handleReject = (status, isActive, rejectReason) => {
      onRejectCourse({
        variables: { 
          guid: course?.guid, 
          isActive: isActive, 
          status: status, 
          rejectReason: rejectReason,
          title: course?.title,
          description: course?.description, 
          language: course?.language, 
          dificultyLevel: course?.dificultyLevel || '', 
          creatorId: course?.creator?.guid, 
          courseStartDate: course?.courseStartDate, 
          courseEndDate: course?.courseEndDate, 
        },
        onCompleted: () => {
          Notify.success("Course rejected successfully!");
          setShowReject(false);
          setRejectReason('');
          window.location.reload();
        },
        onError: () => {
          Notify.failure("Failed to reject course!");
        },
      });
    };

    return (
      <>
        <Dropdown>
          <Dropdown.Toggle as={CustomToggle}>
            <MoreVertical size="15px" className="text-secondary" />
          </Dropdown.Toggle>
          <Dropdown.Menu align="end" className="modern-dropdown-menu">
            <Dropdown.Header className="dropdown-header-modern">ACTIONS</Dropdown.Header>
            <Dropdown.Item eventKey="1" onClick={() => setShowAccept(true)} className="dropdown-item-modern">
              <CheckCircle size={16} className="me-2 text-success" />
              <span>Approve Course</span>
            </Dropdown.Item>
            <Dropdown.Item eventKey="2" onClick={() => setShowReject(true)} className="dropdown-item-modern">
              <XCircle size={16} className="me-2 text-danger" />
              <span>Reject Course</span>
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item
              eventKey="3"
              onClick={() => navigate(PATH_ADMIN.admin.viewCourseProgress(course?.guid))}
              className="dropdown-item-modern"
            >
              <Clock size={16} className="me-2 text-info" />
              <span>View Progress</span>
            </Dropdown.Item>
            <Dropdown.Item
              eventKey="4"
              onClick={() => navigate(PATH_ADMIN.admin.reviewCourse(course?.guid))}
              className="dropdown-item-modern"
            >
              <Eye size={16} className="me-2 text-primary" />
              <span>View Details</span>
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item eventKey="5" onClick={() => setShowFeedbackForm(true)} className="dropdown-item-modern">
              <FileText size={16} className="me-2" style={{ color: '#8b5cf6' }} />
              <span>Edit Feedback Form</span>
            </Dropdown.Item>
            <Dropdown.Item eventKey="6" onClick={() => setShowSendFeedback(true)} className="dropdown-item-modern">
              <Mail size={16} className="me-2" style={{ color: '#1d9eb0' }} />
              <span>Send Feedback Emails</span>
            </Dropdown.Item>
            <Dropdown.Item eventKey="7" onClick={() => setShowSendCerts(true)} className="dropdown-item-modern">
              <Award size={16} className="me-2" style={{ color: '#F68521' }} />
              <span>Send Certificates</span>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>

        <AlertModal
          isOpen={showReject}
          onClose={() => {
            setShowReject(false);
            setRejectReason('');
          }}
          handleYes={() => {
            if (rejectReason.trim()) {
              handleReject('Rejected', false, rejectReason);
            } else {
              Notify.failure('Please provide a reason for rejection');
            }
          }}
          title="Reject Course"
          message="Please provide a reason for rejecting this course. This will be sent to the instructor."
          isLoading={isRejecting}
          component={
            <div className="mt-3">
              <Form.Label className="fw-semibold">Rejection Reason <span className="text-danger">*</span></Form.Label>
              <Form.Control
                as="textarea" 
                rows={4}
                id="reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Explain why this course is being rejected..."
                required
                className="modern-textarea"
              />
            </div>
          }
        />

        <AlertModal
          isOpen={showAccept}
          onClose={() => setShowAccept(false)}
          handleYes={() => handleUpdate('Active', true)}
          title="Approve Course"
          message="Are you sure you want to approve this course? It will be published and visible to all students."
          isLoading={isUpdating}
        />

        <AlertModal
          isOpen={showSendFeedback}
          onClose={() => setShowSendFeedback(false)}
          handleYes={handleSendFeedbackEmails}
          title="Send Feedback Emails"
          message={`Send the post-course feedback form to all enrolled students of "${course?.title}"?`}
          isLoading={isSendingFeedback}
        />

        <AlertModal
          isOpen={showSendCerts}
          onClose={() => setShowSendCerts(false)}
          handleYes={handleSendCertificates}
          title="Send Certificates"
          message={`Send e-certificates of completion to all students who submitted feedback for "${course?.title}"?`}
          isLoading={isSendingCerts}
        />

        <CourseFeedbackFormModal
          show={showFeedbackForm}
          onHide={() => setShowFeedbackForm(false)}
          course={course}
          onSaved={() => window.location.reload()}
        />
      </>
    );
  };

  const columns = useMemo(
    () => [
      {
        header: "Course",
        accessorKey: "title",
        cell: ({ row, getValue }) => {
          return (
            <div
              onClick={() =>
                navigate(PATH_ADMIN.admin.reviewCourse(row?.original?.guid))
              }
              className="course-cell"
            >
              <div className="d-flex align-items-center gap-3">
                <div className="course-thumbnail-wrapper">
                  <Image
                    src={row.original?.introImage[0]}
                    alt={getValue()}
                    className="course-thumbnail-admin"
                  />
                </div>
                <div className="course-info">
                  <h5 className="course-title-admin mb-1">
                    {truncateTextByCount(getValue(), 10)}
                  </h5>
                  <div className="course-meta-admin">
                    <span className="meta-badge">
                      <i className="fe fe-calendar me-1"></i>
                      Created {moment(row.original?.createdAt).fromNow()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        },
      },
      {
        header: "Instructor",
        accessorKey: "creator",
        cell: ({ getValue }) => (
          <div className="instructor-cell">
            <div className="d-flex align-items-center gap-2">
              <Avatar
                size="md"
                className="rounded-circle"
                type="initial"
                alt={getValue()?.firstName}
                name={`${getValue()?.firstName} ${getValue()?.lastName}`}
              />
              <div>
                <h6 className="mb-0 instructor-name">
                  {`${getValue()?.firstName} ${getValue()?.lastName}`}
                </h6>
                <small className="text-muted instructor-email">
                  {getValue()?.email}
                </small>
              </div>
            </div>
          </div>
        ),
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ getValue }) => {
          const status = getValue();
          const statusLower = status.toLowerCase();
          let bgClass = '';
          let icon = '';
          
          if (statusLower === 'active') {
            bgClass = 'status-badge-success';
            icon = 'fe-check-circle';
          } else if (statusLower === 'submitted') {
            bgClass = 'status-badge-warning';
            icon = 'fe-clock';
          } else if (statusLower === 'rejected') {
            bgClass = 'status-badge-danger';
            icon = 'fe-x-circle';
          } else {
            bgClass = 'status-badge-info';
            icon = 'fe-info';
          }

          return (
            <Badge bg="" className={`status-badge-modern ${bgClass}`}>
              <i className={`fe ${icon} me-1`}></i>
              {status}
            </Badge>
          );
        },
      },
      {
        accessorKey: "courseStartDate",
        header: "Start Date",
        cell: ({ getValue }) => {
          return (
            <div className="date-cell">
              <i className="fe fe-calendar text-muted me-2"></i>
              {moment(getValue()).format("MMM DD, YYYY")}
            </div>
          );
        },
      },
      {
        accessorKey: "courseEndDate",
        header: "End Date",
        cell: ({ getValue }) => {
          return (
            <div className="date-cell">
              <i className="fe fe-calendar text-muted me-2"></i>
              {moment(getValue()).format("MMM DD, YYYY")}
            </div>
          );
        },
      },
      {
        accessorKey: "shortcutmenu",
        header: "",
        cell: ({ row }) => {
          return <ActionMenu course={row.original} />;
        },
      },
    ],
    []
  );

  const data = useMemo(() => courses_data, [courses_data]);

  return (
    <>
      <TanstackTable
        data={data}
        columns={columns}
        filter={true}
        filterPlaceholder="Search courses by title, instructor..."
        pagination={true}
      />

      <style jsx global>{`
        /* Course Cell */
        .course-cell {
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .course-cell:hover .course-title-admin {
          color: #F68521;
        }

        .course-thumbnail-wrapper {
          position: relative;
          flex-shrink: 0;
        }

        .course-thumbnail-admin {
          width: 80px;
          height: 60px;
          object-fit: cover;
          border-radius: 8px;
          transition: transform 0.3s ease;
        }

        .course-cell:hover .course-thumbnail-admin {
          transform: scale(1.05);
        }

        .course-info {
          flex: 1;
        }

        .course-title-admin {
          font-size: 0.9375rem;
          font-weight: 700;
          color: #1e293b;
          line-height: 1.4;
          transition: color 0.3s ease;
          margin: 0;
        }

        .course-meta-admin {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .meta-badge {
          font-size: 0.75rem;
          color: #94a3b8;
          display: flex;
          align-items: center;
        }

        /* Instructor Cell */
        .instructor-cell {
          /* Handled by children */
        }

        .instructor-name {
          font-size: 0.9375rem;
          font-weight: 600;
          color: #1e293b;
        }

        .instructor-email {
          font-size: 0.8125rem;
          display: block;
        }

        /* Status Badges */
        .status-badge-modern {
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          display: inline-flex;
          align-items: center;
        }

        .status-badge-success {
          background: rgba(16, 185, 129, 0.1) !important;
          color: #10b981 !important;
        }

        .status-badge-warning {
          background: rgba(251, 191, 36, 0.1) !important;
          color: #fbbf24 !important;
        }

        .status-badge-danger {
          background: rgba(239, 68, 68, 0.1) !important;
          color: #ef4444 !important;
        }

        .status-badge-info {
          background: rgba(29, 158, 176, 0.1) !important;
          color: #1d9eb0 !important;
        }

        /* Date Cell */
        .date-cell {
          font-size: 0.875rem;
          color: #475569;
          display: flex;
          align-items: center;
        }

        /* Action Toggle */
        .action-toggle {
          transition: all 0.3s ease;
        }

        .action-toggle:hover {
          background: rgba(246, 133, 33, 0.1) !important;
          transform: rotate(90deg);
        }

        /* Modern Dropdown */
        .modern-dropdown-menu {
          border: none;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
          border-radius: 12px;
          padding: 0.5rem;
          min-width: 220px;
        }

        .dropdown-header-modern {
          font-size: 0.6875rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #94a3b8;
          padding: 0.5rem 1rem;
        }

        .dropdown-item-modern {
          padding: 0.75rem 1rem;
          border-radius: 8px;
          font-size: 0.9375rem;
          font-weight: 500;
          display: flex;
          align-items: center;
          transition: all 0.2s ease;
        }

        .dropdown-item-modern:hover {
          background: rgba(246, 133, 33, 0.1);
          color: #F68521;
        }

        .dropdown-item-modern span {
          flex: 1;
        }

        /* Modern Textarea */
        .modern-textarea {
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          padding: 0.875rem;
          font-size: 0.9375rem;
          resize: none;
          transition: all 0.3s ease;
        }

        .modern-textarea:focus {
          border-color: #F68521;
          box-shadow: 0 0 0 3px rgba(246, 133, 33, 0.1);
        }

        /* Table Enhancements */
        .table tbody tr {
          transition: all 0.2s ease;
        }

        .table tbody tr:hover {
          background: #f8fafc;
        }
      `}</style>
    </>
  );
};

export default CoursesTable;